import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeft, Plus, Pencil, Trash2, FlaskConical, Building } from 'lucide-react';

const emptyVendor = {
  name: '',
  code: '',
  contactPerson: '',
  phone: '',
  email: '',
  address: '',
  city: ''
};

const VendorsPage = () => {
  const { api, user } = useAuth();
  const [activeTab, setActiveTab] = useState('pathology');
  const [pathologyVendors, setPathologyVendors] = useState([]);
  const [radiologyVendors, setRadiologyVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [vendorForm, setVendorForm] = useState(emptyVendor);
  const [submitting, setSubmitting] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setError('');
      const res = await api.get('/vendors/stats');
      setPathologyVendors(res.data.pathology || []);
      setRadiologyVendors(res.data.radiology || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load vendors');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const vendors = activeTab === 'pathology' ? pathologyVendors : radiologyVendors;
  const apiBase = activeTab === 'pathology' ? '/pathology/providers' : '/radiology/providers';

  const totals = useMemo(() => {
    return vendors.reduce((acc, v) => ({
      tests: acc.tests + (v.testsPerformed || 0),
      bookings: acc.bookings + (v.bookingCount || 0),
      revenue: acc.revenue + (v.revenue || 0)
    }), { tests: 0, bookings: 0, revenue: 0 });
  }, [vendors]);

  const resetForm = () => {
    setVendorForm(emptyVendor);
    setEditingVendor(null);
  };

  const openCreate = () => {
    resetForm();
    setShowForm(true);
  };

  const openEdit = (vendor) => {
    setEditingVendor(vendor);
    setVendorForm({
      name: vendor.name || '',
      code: vendor.code || '',
      contactPerson: vendor.contactPerson || '',
      phone: vendor.phone || '',
      email: vendor.email || '',
      address: vendor.address || '',
      city: vendor.city || ''
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setError('');
      setSubmitting(true);
      if (editingVendor) {
        await api.put(`${apiBase}/${editingVendor._id}`, vendorForm);
      } else {
        await api.post(apiBase, vendorForm);
      }
      setShowForm(false);
      resetForm();
      fetchData();
    } catch (err) {
      setError(err.response?.data?.message || `Failed to ${editingVendor ? 'update' : 'create'} vendor`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (vendor) => {
    if (!window.confirm(`Are you sure you want to remove vendor "${vendor.name}"?`)) return;
    try {
      setError('');
      await api.delete(`${apiBase}/${vendor._id}`);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete vendor');
    }
  };

  const canManage = user?.role === 'hospital_admin' || user?.role === 'super_admin';

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button onClick={() => window.history.back()} className="p-2 hover:bg-gray-100 rounded-lg">
                <ArrowLeft className="h-5 w-5" />
              </button>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center">
                <FlaskConical className="h-6 w-6 mr-2" />
                Lab Vendors
              </h1>
            </div>
            {canManage && (
              <button
                onClick={openCreate}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center hover:bg-blue-700"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Vendor
              </button>
            )}
          </div>
          <div className="flex space-x-1 mt-4">
            <button
              onClick={() => setActiveTab('pathology')}
              className={`px-4 py-2 rounded-t-lg font-medium text-sm ${activeTab === 'pathology' ? 'bg-gray-100 text-gray-900 border border-b-0 border-gray-300' : 'text-gray-500 hover:text-gray-700'}`}
            >
              Pathology
            </button>
            <button
              onClick={() => setActiveTab('radiology')}
              className={`px-4 py-2 rounded-t-lg font-medium text-sm ${activeTab === 'radiology' ? 'bg-gray-100 text-gray-900 border border-b-0 border-gray-300' : 'text-gray-500 hover:text-gray-700'}`}
            >
              Radiology
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
            {error}
          </div>
        )}

        <div className="flex flex-wrap gap-4 mb-6">
          <div className="flex-1 bg-white p-4 rounded-lg shadow" style={{ minWidth: '12rem' }}>
            <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Vendors</p>
            <p className="text-2xl font-bold text-gray-900">{vendors.length}</p>
          </div>
          <div className="flex-1 bg-blue-50 p-4 rounded-lg shadow" style={{ minWidth: '12rem' }}>
            <p className="text-xs font-semibold text-blue-800 uppercase tracking-wider">Tests Performed</p>
            <p className="text-2xl font-bold text-blue-800">{totals.tests}</p>
          </div>
          <div className="flex-1 bg-purple-50 p-4 rounded-lg shadow" style={{ minWidth: '12rem' }}>
            <p className="text-xs font-semibold text-purple-800 uppercase tracking-wider">Bookings</p>
            <p className="text-2xl font-bold text-purple-800">{totals.bookings}</p>
          </div>
          <div className="flex-1 bg-green-50 p-4 rounded-lg shadow" style={{ minWidth: '12rem' }}>
            <p className="text-xs font-semibold text-green-800 uppercase tracking-wider">Test Value</p>
            <p className="text-2xl font-bold text-green-800">₹{totals.revenue.toFixed(2)}</p>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="p-6">
            <h2 className="text-lg font-semibold mb-4">
              {activeTab === 'pathology' ? 'Pathology' : 'Radiology'} Vendors
            </h2>
            {loading ? (
              <p className="text-center py-8 text-gray-500">Loading...</p>
            ) : vendors.length === 0 ? (
              <div className="text-center py-12">
                <Building className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No {activeTab} vendors added yet</p>
                {canManage && (
                  <button onClick={openCreate} className="mt-3 text-blue-600 hover:underline text-sm">
                    Add your first vendor
                  </button>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4">Vendor</th>
                      <th className="text-left py-3 px-4">Code</th>
                      <th className="text-left py-3 px-4">Contact</th>
                      <th className="text-left py-3 px-4">City</th>
                      <th className="text-left py-3 px-4">Tests Performed</th>
                      <th className="text-left py-3 px-4">Bookings</th>
                      <th className="text-left py-3 px-4">Test Value</th>
                      {canManage && <th className="text-left py-3 px-4">Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {vendors.map((vendor) => (
                      <tr key={vendor._id} className="border-b hover:bg-gray-50">
                        <td className="py-3 px-4">
                          <p className="font-medium">{vendor.name}</p>
                          {vendor.email && <p className="text-xs text-gray-500">{vendor.email}</p>}
                        </td>
                        <td className="py-3 px-4 font-mono text-sm">{vendor.code || '-'}</td>
                        <td className="py-3 px-4">
                          {vendor.contactPerson && <p className="text-sm">{vendor.contactPerson}</p>}
                          {vendor.phone && <p className="text-xs text-gray-500">{vendor.phone}</p>}
                          {!vendor.contactPerson && !vendor.phone && <span className="text-gray-400">-</span>}
                        </td>
                        <td className="py-3 px-4">{vendor.city || '-'}</td>
                        <td className="py-3 px-4">
                          <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-semibold">
                            {vendor.testsPerformed}
                          </span>
                        </td>
                        <td className="py-3 px-4">{vendor.bookingCount}</td>
                        <td className="py-3 px-4">₹{(vendor.revenue || 0).toFixed(2)}</td>
                        {canManage && (
                          <td className="py-3 px-4">
                            <div className="flex gap-2">
                              <button
                                onClick={() => openEdit(vendor)}
                                className="bg-gray-600 text-white p-2 rounded hover:bg-gray-700"
                                title="Edit"
                              >
                                <Pencil className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleDelete(vendor)}
                                className="bg-red-600 text-white p-2 rounded hover:bg-red-700"
                                title="Remove"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => { setShowForm(false); resetForm(); }}>
          <div className="bg-white rounded-lg p-6 w-full max-w-lg mx-4 shadow-lg" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">
                {editingVendor ? 'Edit' : 'Add'} {activeTab === 'pathology' ? 'Pathology' : 'Radiology'} Vendor
              </h2>
              <button
                type="button"
                onClick={() => { setShowForm(false); resetForm(); }}
                className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
              >
                &times;
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Vendor Name *</label>
                    <input
                      type="text"
                      value={vendorForm.name}
                      onChange={(e) => setVendorForm({ ...vendorForm, name: e.target.value })}
                      placeholder={activeTab === 'pathology' ? 'e.g. Dr. Lal PathLabs' : 'e.g. City Imaging Center'}
                      className="form-input w-full"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Code</label>
                    <input
                      type="text"
                      value={vendorForm.code}
                      onChange={(e) => setVendorForm({ ...vendorForm, code: e.target.value.toUpperCase() })}
                      placeholder="AUTO"
                      className="form-input w-full"
                      style={{ textTransform: 'uppercase' }}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Contact Person</label>
                    <input
                      type="text"
                      value={vendorForm.contactPerson}
                      onChange={(e) => setVendorForm({ ...vendorForm, contactPerson: e.target.value })}
                      className="form-input w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                    <input
                      type="text"
                      value={vendorForm.phone}
                      onChange={(e) => setVendorForm({ ...vendorForm, phone: e.target.value })}
                      className="form-input w-full"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input
                      type="email"
                      value={vendorForm.email}
                      onChange={(e) => setVendorForm({ ...vendorForm, email: e.target.value })}
                      className="form-input w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                    <input
                      type="text"
                      value={vendorForm.city}
                      onChange={(e) => setVendorForm({ ...vendorForm, city: e.target.value })}
                      className="form-input w-full"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                  <textarea
                    value={vendorForm.address}
                    onChange={(e) => setVendorForm({ ...vendorForm, address: e.target.value })}
                    rows="2"
                    className="form-input w-full"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <button
                  type="button"
                  onClick={() => { setShowForm(false); resetForm(); }}
                  disabled={submitting}
                  className="btn-secondary px-4 py-2 rounded disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="bg-green-600 text-white px-6 py-2 rounded hover:bg-green-700 font-semibold disabled:opacity-60 flex items-center"
                >
                  {submitting ? (
                    <>
                      <svg className="animate-spin h-4 w-4 mr-2" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      {editingVendor ? 'Updating...' : 'Creating...'}
                    </>
                  ) : (
                    editingVendor ? 'Update Vendor' : 'Create Vendor'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorsPage;
